// brew-helpers.js — derived helpers for the coffee learning loop.
//
// Pure functions, no I/O. Composed by useStore and the Coffee module.

// Aiden cup convention: 1 cup ≈ 150g brewed.
// Aiden modes: single-serve = 1–3 cups, batch = 3–10 cups (3 overlaps; we use
// batch when cups >= 4 to give the carafe room).
const ML_PER_CUP = 150;

// Cups are stored in 0.5 increments (1.0, 1.5, 2.0, …, 10.0). Aiden's UI
// also accepts half-cups; the math is just water = cups * 150g.
export function cupsToWaterMl(cups) {
  const c = Math.max(1, Math.min(10, Math.round((+cups || 0) * 2) / 2));
  return Math.round(c * ML_PER_CUP);
}

export function waterMlToCups(ml) {
  // Snap to nearest 0.5
  return Math.round(((ml || 0) / ML_PER_CUP) * 2) / 2;
}

export function isBatchMode(cups) {
  return cups >= 4;
}

// Hot vs cold-brew profile selection based on user pick + auto-bias from weather.
// hot mode picks a sensible default profile; cold always picks "cold-brew".
// suggested = a profileId the system thinks fits (from weather); pick = user override.
export function pickProfileForMode({ mode, suggestedProfileId, fallback = "balanced" }) {
  if (mode === "cold") return "cold-brew";
  return suggestedProfileId || fallback;
}

// Bag depletion forecast.
// Given a bean's bag size, weight remaining, and recent brews against it,
// return: brews_remaining, days_remaining (at current daily rate), should_reorder.
export function forecastDepletion(bean, brewLogs) {
  if (!bean) return null;
  const weightLeft = +(bean.weight_left_g ?? bean.weight_g ?? 0);
  if (!weightLeft) return null;

  // Average dose per brew on this bean over the last 14 days
  const since = Date.now() - 14 * 86400 * 1000;
  const recent = (brewLogs || []).filter(
    l => l.bean_id === bean.id && new Date(l.brewed_at).getTime() >= since
  );

  let avgDose = 18;
  if (recent.length > 0) {
    avgDose = recent.reduce((s, l) => s + (+l.dose_g || 0), 0) / recent.length;
  }
  const brewsRemaining = Math.floor(weightLeft / avgDose);

  // Brews per day rate based on the last 14 days
  let brewsPerDay = 1;
  if (recent.length > 0) {
    brewsPerDay = recent.length / 14;
    if (brewsPerDay < 0.1) brewsPerDay = 0.1; // never project >140 days
  }
  const daysRemaining = brewsPerDay > 0 ? brewsRemaining / brewsPerDay : null;

  return {
    weight_left_g: weightLeft,
    weight_total_g: +(bean.weight_g ?? weightLeft),
    pct_remaining: Math.round((weightLeft / Math.max(1, +(bean.weight_g ?? weightLeft))) * 100),
    avg_dose_g: +avgDose.toFixed(1),
    brews_remaining: brewsRemaining,
    brews_per_day: +brewsPerDay.toFixed(2),
    days_remaining: daysRemaining ? Math.round(daysRemaining) : null,
    should_reorder: brewsRemaining <= 5,
  };
}

// Decrement bean weight when a brew is logged.
export function decrementBeanWeight(bean, doseG) {
  const left = Math.max(0, (+bean.weight_left_g || +bean.weight_g || 0) - (+doseG || 0));
  return { ...bean, weight_left_g: left };
}

// Find bags with weight remaining (physically not empty).
export function findActiveBags(beans) {
  return (beans || []).filter(b => {
    const w = +(b.weight_left_g ?? b.weight_g ?? 0);
    return w > 0;
  });
}

// The bags currently OPEN — the ones actually available to brew (in Josh's
// vacuum container). He marks these explicitly via preferences.open_bean_ids.
// Until he ever does (ids undefined), fall back to "any bag with weight left"
// so the app still works out of the box. An explicit empty array means
// "nothing open" and is respected.
export function getOpenBeans(beans, preferences) {
  const ids = preferences?.open_bean_ids;
  const list = beans || [];
  if (Array.isArray(ids)) {
    const set = new Set(ids);
    return list.filter(b => set.has(b.id));
  }
  return findActiveBags(list);
}

// Canister true-up: net beans = gross scale reading − canister tare. Returns
// null if we can't compute (missing/invalid inputs) so callers can guard.
export function canisterNet(grossG, tareG) {
  const g = +grossG, t = +tareG;
  if (!Number.isFinite(g) || !Number.isFinite(t)) return null;
  return Math.max(0, +(g - t).toFixed(1));
}

// Which bean is physically in the canister right now: the open bag Josh last
// brewed, else the first open bag. null if nothing is open.
export function canisterBean(beans, preferences) {
  const open = getOpenBeans(beans, preferences);
  if (!open.length) return null;
  return open.find(b => b.id === preferences?.last_bean_id) || open[0];
}

// Should this bean be reordered? True when it's below the canister's gram
// threshold, or (no threshold set) when the depletion forecast says so. Per
// product: each bean carries its own weight + trailing usage.
export function needsReorder(bean, forecast, thresholdG) {
  if (!bean) return false;
  const left = +(bean.weight_left_g ?? bean.weight_g ?? 0);
  if (Number.isFinite(+thresholdG) && +thresholdG > 0) return left < +thresholdG;
  return !!forecast?.should_reorder;
}

// Roaster aggregate score from check-ins.
// Returns Map<roaster, {avg_rating, samples, top_origins[]}>
export function aggregateRoasterScores(beans, checkIns) {
  const beansById = new Map(beans.map(b => [b.id, b]));
  const byRoaster = new Map();

  for (const c of checkIns || []) {
    const bean = beansById.get(c.bean_id);
    const roaster = bean?.roaster?.trim();
    if (!roaster || c.rating == null) continue;

    if (!byRoaster.has(roaster)) {
      byRoaster.set(roaster, { ratings: [], origins: new Map() });
    }
    const agg = byRoaster.get(roaster);
    agg.ratings.push(+c.rating);

    for (const origin of bean.origin || []) {
      agg.origins.set(origin, (agg.origins.get(origin) || 0) + 1);
    }
  }

  const out = new Map();
  for (const [roaster, agg] of byRoaster.entries()) {
    const avg = agg.ratings.reduce((s, r) => s + r, 0) / agg.ratings.length;
    const top = [...agg.origins.entries()].sort((a, b) => b[1] - a[1]).slice(0, 3).map(e => e[0]);
    out.set(roaster, {
      avg_rating: +avg.toFixed(2),
      samples: agg.ratings.length,
      top_origins: top,
    });
  }
  return out;
}

// Score by origin (independent of roaster)
export function aggregateOriginScores(beans, checkIns) {
  const beansById = new Map(beans.map(b => [b.id, b]));
  const byOrigin = new Map();

  for (const c of checkIns || []) {
    const bean = beansById.get(c.bean_id);
    if (c.rating == null || !bean) continue;
    for (const origin of bean.origin || []) {
      if (!byOrigin.has(origin)) byOrigin.set(origin, []);
      byOrigin.get(origin).push(+c.rating);
    }
  }

  const out = new Map();
  for (const [origin, ratings] of byOrigin.entries()) {
    const avg = ratings.reduce((s, r) => s + r, 0) / ratings.length;
    out.set(origin, { avg_rating: +avg.toFixed(2), samples: ratings.length });
  }
  return out;
}

// When user adds a new bean, find their best matches from history.
export function insightForNewBean({ roaster, origin = [] }, roasterScores, originScores) {
  const lines = [];

  if (roaster && roasterScores.has(roaster)) {
    const r = roasterScores.get(roaster);
    if (r.samples >= 2) {
      lines.push({
        type: "roaster",
        text: `You've rated ${roaster} ${r.avg_rating.toFixed(1)}★ across ${r.samples} brews.`,
        score: r.avg_rating,
      });
    }
  }

  for (const o of origin) {
    if (originScores.has(o)) {
      const stats = originScores.get(o);
      if (stats.samples >= 2) {
        lines.push({
          type: "origin",
          text: `${o} beans average ${stats.avg_rating.toFixed(1)}★ for you (${stats.samples} brews).`,
          score: stats.avg_rating,
        });
      }
    }
  }

  return lines;
}
